import { Component, Input, OnInit, inject } from '@angular/core';

import {
    Faim,
    FaimPremium,
    Plan,
    ProductFactory,
    PromotionModel,
    PromotionsService,
    QuoteModel,
    Sim,
    TYPE_MARKETING,
} from '@bytel/bytel-sales';
import { CheckoutStorageService, GalliumService, Oauth2Service, QuoteContextRenewalModel } from '@bytel/checkout';

import { PriceComponent } from '@base/trilogy/price/price.component';

import { MainCartService } from '@services/main-cart.service';
import { OptionsService } from '@services/options.service';

import { ObligationPipe } from '../../../../shared/pipes/obligation.pipe';
import { PromoHomeService } from '../promo-home.service';
import { PricesBlockComponent } from './prices-block/prices-block/prices-block.component';

// @dynamic
@Component({
    selector: 'checkout-subscription',
    templateUrl: './subscription.component.html',
    styleUrl: 'subscription.component.scss',
    standalone: true,
    imports: [PricesBlockComponent, ObligationPipe, PriceComponent],
})
export class SubscriptionComponent implements OnInit {
    @Input() subscription: Plan;
    public isPlanPro: boolean;

    public isRenewal: boolean = false;
    public isMigratingPlan: boolean = false;
    public isLocked: boolean = false;
    public showSimCard: boolean = true;
    public isFaimType: boolean;
    public isConnected: boolean = false;
    public canHavePromoHome: boolean = false;
    public milanoPromo: PromotionModel;
    public incitationPromos: PromotionModel[] = [];
    public automaticPromos: PromotionModel[] = [];
    public promoHome: PromotionModel;
    public sim: Sim;
    public subscriptionLabel: string = '';
    public convergencePromoAmount: number = 0;
    public canHaveConvergence: boolean = false;

    private cartService: MainCartService = inject(MainCartService);
    private oauth2Service: Oauth2Service = inject(Oauth2Service);
    private galliumService: GalliumService = inject(GalliumService);
    private promoHomeService: PromoHomeService = inject(PromoHomeService);
    private storageService: CheckoutStorageService = inject(CheckoutStorageService);

    public ngOnInit(): void {
        this.isConnected = this.oauth2Service.isConnected;
        this.isFaimType =
            ProductFactory.Is(this.subscription, Faim) || ProductFactory.Is(this.subscription, FaimPremium);
        this.isPlanPro = this.subscription?.isPro;
        const quote: QuoteModel = this.cartService.getQuote();
        const plan: Plan = quote.getPrincipalProduct<Plan>('Plan');

        this.sim = quote.getProductByType(Sim);
        this.isLocked = OptionsService.HasBlockedOption(this.cartService.cartModel) || plan.data.locked === '2';
        this.isRenewal = quote.isRenewal();
        this.showSimCard = !this.isRenewal && !ProductFactory.Is(plan, Faim) && !ProductFactory.Is(plan, FaimPremium);

        this.isMigratingPlan = this.cartService.isMigratingPlan();
        const promos = PromotionsService.GetPromotionsForProduct(
            this.subscription,
            this.cartService.cartModel.promotions,
        );

        this.incitationPromos = promos?.incitation;
        this.automaticPromos = promos?.automatic;
        this.promoHome = this.promoHomeService.getApplicablePromoHome(
            this.subscription,
            this.cartService.cartModel.promotions,
            this.isConnected,
        );
        this.convergencePromoAmount = this._getPromoConvergenceAmount();
        this.canHavePromoHome = !!this.promoHome;

        this.milanoPromo = this._getPromoMilano();
        this.subscriptionLabel = this.subscription?.label;

        if (this.isRenewal) {
            const galliumData = this.galliumService.getGalliumData(
                plan.data.gencode,
                (this.cartService.getQuote(0).context as QuoteContextRenewalModel).contractId,
                !this.isMigratingPlan,
            );
            this.subscriptionLabel = galliumData?.libelleCommercial || this.subscription?.label;
        }
    }

    private _getPromoMilano(): PromotionModel {
        return this.incitationPromos?.find((promo) => promo?.typesMarketing?.includes(TYPE_MARKETING.MULTILIGNES));
    }

    private _getPromoConvergenceAmount(): number {
        // l'affichage de la promo se fait uniquement pour un prospect NON CONNECTÉ !
        // Sinon, si le client est connecté et éligible, la remise convergence est incluse dans le prix

        this.canHaveConvergence =
            !this.isConnected &&
            (this.storageService.get('promoConfigurator', false)?.convergence || false) &&
            (!this.promoHome || this.promoHome?.amount === 0);

        if (!this.canHaveConvergence) {
            return 0;
        }

        return (
            this.incitationPromos.find((promo) => promo.typesMarketing.includes(TYPE_MARKETING.CONVERGENCE))?.amount ||
            0
        );
    }
}
